import React, { useState } from "react";
import useTranslation from "next-translate/useTranslation";
import { Button } from "@/components/ui/button";

/** Secrets stay in memory. Never send the code to analytics or persistent browser storage. */
export default function RecoveryCodePanel({
  code,
  username,
  onSaved,
}: {
  code: string;
  username: string;
  onSaved?: (saved: boolean) => void;
}) {
  const { t } = useTranslation("auth");
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const download = () => {
    const text = `Chess Hotel\n${t("Username")}: ${username}\n${t(
      "recoveryCodeLabel"
    )}: ${code}\n\n${t("recoveryCodePrivate")}`;
    const url = URL.createObjectURL(
      new Blob([text], { type: "text/plain;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "chesshotel-recovery.txt";
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section
      className="ym-hide-content w-full rounded-xl border border-altgray-200 bg-altgray-50 p-4 text-left"
      data-private
      data-hj-suppress
    >
      <h2 className="font-semibold">{t("saveRecoveryCode")}</h2>
      <p className="mt-2 text-sm text-altgray-600">
        {t("recoveryCodeExplanation")}
      </p>
      <p className="mt-3 text-sm">
        {t("Username")}: <strong>{username}</strong>
      </p>
      <label
        className="mt-4 block text-sm font-medium"
        htmlFor="account-recovery-code"
      >
        {t("recoveryCodeLabel")}
      </label>
      <input
        id="account-recovery-code"
        type={revealed ? "text" : "password"}
        readOnly
        value={code}
        autoComplete="off"
        spellCheck={false}
        className="ym-disable-keys mt-1 w-full rounded border bg-white p-2 font-mono text-sm"
      />
      <button
        type="button"
        className="mt-2 text-sm underline"
        onClick={() => setRevealed(!revealed)}
      >
        {t(revealed ? "hideRecoveryCode" : "showRecoveryCode")}
      </button>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(code);
              setCopied(true);
              setCopyFailed(false);
            } catch {
              setCopyFailed(true);
            }
          }}
        >
          {t(copied ? "recoveryCodeCopied" : "copyRecoveryCode")}
        </Button>
        <Button variant="outline" onClick={download}>
          {t("downloadRecoveryCode")}
        </Button>
      </div>
      {copyFailed && (
        <p role="status" className="mt-2 text-sm">
          {t("recoveryCodeCopyFailed")}
        </p>
      )}
      <p className="mt-3 text-sm text-altgray-600">
        {t("recoveryCodePrivate")}
      </p>
      <label className="mt-3 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={saved}
          onChange={(e) => {
            setSaved(e.target.checked);
            onSaved?.(e.target.checked);
          }}
          className="mt-1"
        />
        {t("recoveryCodeSaved")}
      </label>
    </section>
  );
}
